import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, MapPin, DoorOpen, Star } from "lucide-react";
import apiClient from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import PropertyStatusBadge, { PROPERTY_TYPE_LABELS, GENDER_LABELS } from "@/components/property/PropertyStatusBadge";
import { formatRupiah } from "@/components/property/roomMeta";

const FILTERS = [
  { key: "pending", label: "Menunggu Review" },
  { key: "published", label: "Aktif" },
  { key: "rejected", label: "Ditolak" },
  { key: "inactive", label: "Nonaktif" },
  { key: "all", label: "Semua" },
];

const FEATURED_FILTERS = [
  { key: "", label: "Semua" },
  { key: "featured", label: "Featured" },
  { key: "not_featured", label: "Tidak Featured" },
];

export default function AdminPropertyModerationPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("pending");
  const [featuredFilter, setFeaturedFilter] = useState("");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const loadStats = useCallback(() => {
    apiClient.get("/admin/properties/stats").then((r) => setStats(r.data.properties)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filter !== "all") params.status = filter;
    if (featuredFilter) params.featured = featuredFilter;
    if (search.trim()) params.search = search.trim();
    apiClient
      .get("/admin/properties", { params })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [filter, featuredFilter, search]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const toggleFeatured = async (p) => {
    setBusyId(p.id);
    try {
      await apiClient.patch(`/admin/properties/${p.id}/featured`, {
        is_featured: !p.is_featured,
        featured_order: p.featured_order || 0,
      });
      setItems((prev) => prev.map((it) => it.id === p.id ? { ...it, is_featured: !p.is_featured } : it));
    } catch { /* ignore */ }
    finally { setBusyId(null); }
  };

  return (
    <AdminLayout testid="admin-moderation-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight">Moderasi Properti</h1>
      <p className="mt-2 text-sm text-muted-foreground">Tinjau, setujui, atau tolak listing yang diajukan Owner.</p>

      {/* Filter tabs with counts */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="moderation-filters">
        {FILTERS.map((f) => {
          const count = stats ? (f.key === "all" ? stats.total : stats[f.key]) : null;
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                active ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary/60"
              }`}
            >
              {f.label}{count != null && ` (${count})`}
            </button>
          );
        })}
      </div>

      {/* Featured filter */}
      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="moderation-featured-filters">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mr-1">Iklan:</span>
        {FEATURED_FILTERS.map((f) => (
          <button key={f.key || "all"} onClick={() => setFeaturedFilter(f.key)}
            data-testid={`featured-filter-${f.key || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              featuredFilter === f.key ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:bg-secondary/60"
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mt-4 relative max-w-md">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cari nama property atau owner..."
          data-testid="moderation-search"
          className="w-full rounded-full border border-border bg-secondary/40 pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary focus:bg-background"
        />
      </div>

      {/* List */}
      <div className="mt-6">
        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-primary" size={26} /></div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-secondary/30 p-12 text-center" data-testid="moderation-empty">
            <p className="text-sm text-muted-foreground">Tidak ada property pada filter ini.</p>
          </div>
        ) : (
          <div className="grid gap-3" data-testid="moderation-list">
            {items.map((p, i) => (
              <div key={p.id} data-testid={`moderation-item-${i}`}
                className="bg-card border border-border rounded-2xl p-5 flex flex-wrap items-start justify-between gap-4 hover:shadow-md transition-shadow">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-lg font-semibold" data-testid={`moderation-name-${i}`}>{p.name}</h3>
                    <PropertyStatusBadge status={p.status} testid={`moderation-status-${i}`} />
                    {p.is_featured && (
                      <span data-testid={`moderation-featured-badge-${i}`}
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-accent/15 text-accent border border-accent/30">
                        <Star size={11} className="fill-accent" /> Featured
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Owner: <span className="font-medium text-foreground" data-testid={`moderation-owner-${i}`}>{p.owner_name}</span> · {p.owner_email}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1"><MapPin size={14} /> {p.city_name}, {p.district_name}</span>
                    <span>{PROPERTY_TYPE_LABELS[p.property_type]}</span>
                    <span>{GENDER_LABELS[p.gender_type]}</span>
                    <span className="flex items-center gap-1"><DoorOpen size={14} /> {p.room_count} kamar</span>
                    <span className="font-semibold text-primary">{formatRupiah(p.min_price)}/bln</span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {p.status === "published" && (
                    <button
                      onClick={() => toggleFeatured(p)}
                      disabled={busyId === p.id}
                      data-testid={`moderation-featured-toggle-${i}`}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition-transform hover:scale-105 disabled:opacity-60 inline-flex items-center gap-1.5 ${
                        p.is_featured ? "border border-border text-muted-foreground bg-secondary/40" : "bg-primary text-primary-foreground"
                      }`}
                    >
                      <Star size={14} className={p.is_featured ? "" : "fill-current"} />
                      {p.is_featured ? "Nonaktifkan Featured" : "Jadikan Featured"}
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/admin/properties/${p.id}`)}
                    data-testid={`moderation-view-${i}`}
                    className="px-4 py-2 rounded-full bg-accent text-accent-foreground text-sm font-semibold hover:scale-105 transition-transform"
                  >
                    Lihat Detail
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
